import { useCallback, useState } from 'react';

import { api } from '../../services/api';
import type { Chat, Message } from '../../types';

type Props = {
  activeChat: Chat;
  messages: Message[];
  onInsertDraft: (content: string) => void;
};

type AIFeature = 'suggest' | 'summarize' | 'analyze' | 'extract' | 'translate';

const AIAssistant = ({ activeChat, messages, onInsertDraft }: Props) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<null | AIFeature>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runFeature = useCallback(
    async (feature: AIFeature) => {
      if (messages.length === 0) return;
      setLoading(feature);
      setResult(null);
      setError(null);
      try {
        const conversationMessages = messages.slice(-20).map((message) => ({
          role: (message.direction === 'inbound' ? 'user' : 'assistant') as 'user' | 'assistant',
          content: message.content,
        }));
        const output = await api.aiTask({
          task: feature,
          messages: conversationMessages,
          contactId: activeChat.id,
          contactName: activeChat.title,
        });
        setResult(output.result.content);
      } catch {
        setError('AI is niet beschikbaar. Controleer of de AI provider is geconfigureerd.');
      } finally {
        setLoading(null);
      }
    },
    [activeChat.id, activeChat.title, messages],
  );

  if (!open) {
    return (
      <button
        type="button"
        className="ai-hover-button"
        onClick={() => setOpen(true)}
        aria-label="AI assistent openen"
        title="AI assistent"
      >
        <span className="ai-hover-icon" aria-hidden="true">✨</span>
      </button>
    );
  }

  return (
    <>
      <div className="ai-assistant-panel" role="dialog" aria-label="AI assistent">
        <header className="ai-assistant-header">
          <span className="ai-assistant-title">✨ AI Assistent</span>
          <button
            type="button"
            className="button icon small"
            onClick={() => {
              setOpen(false);
              setResult(null);
              setError(null);
            }}
            aria-label="Sluiten"
          >
            ×
          </button>
        </header>
        <div className="ai-assistant-body">
          <div className="ai-feature-grid">
            <button
              type="button"
              className="ai-feature-btn"
              onClick={() => void runFeature('suggest')}
              disabled={loading !== null || messages.length === 0}
            >
              <span aria-hidden="true">💬</span> Reply suggestie
            </button>
            <button
              type="button"
              className="ai-feature-btn"
              onClick={() => void runFeature('summarize')}
              disabled={loading !== null || messages.length === 0}
            >
              <span aria-hidden="true">📋</span> Samenvatten
            </button>
            <button
              type="button"
              className="ai-feature-btn"
              onClick={() => void runFeature('analyze')}
              disabled={loading !== null || messages.length === 0}
            >
              <span aria-hidden="true">🔍</span> Analyseren
            </button>
            <button
              type="button"
              className="ai-feature-btn"
              onClick={() => void runFeature('extract')}
              disabled={loading !== null || messages.length === 0}
            >
              <span aria-hidden="true">📝</span> Actiepunten
            </button>
            <button
              type="button"
              className="ai-feature-btn"
              onClick={() => void runFeature('translate')}
              disabled={loading !== null || messages.length === 0}
            >
              <span aria-hidden="true">🌐</span> Vertalen
            </button>
          </div>
          {loading && (
            <div className="ai-loading">
              <span className="ai-spinner" />
              {loading === 'suggest' && 'Suggestie genereren…'}
              {loading === 'summarize' && 'Samenvatten…'}
              {loading === 'analyze' && 'Analyseren…'}
              {loading === 'extract' && 'Actiepunten extraheren…'}
              {loading === 'translate' && 'Vertalen…'}
            </div>
          )}
          {error && <p className="notice error small">{error}</p>}
          {result && (
            <div className="ai-result">
              <pre className="ai-result-text">{result}</pre>
              {loading === null && result && (
                <button
                  type="button"
                  className="button primary small"
                  onClick={() => onInsertDraft(result)}
                >
                  ↓ In composer
                </button>
              )}
            </div>
          )}
          {!loading && !result && !error && messages.length === 0 && (
            <p className="muted small">Nog geen berichten in deze chat.</p>
          )}
        </div>
      </div>
      <button
        type="button"
        className="ai-hover-button active"
        onClick={() => {
          setOpen(false);
          setResult(null);
          setError(null);
        }}
        aria-label="AI assistent sluiten"
      >
        <span className="ai-hover-icon" aria-hidden="true">✨</span>
      </button>
    </>
  );
};

export default AIAssistant;
